import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { countries, states } from '../../Components/Enums/DataEnums';
import { toast } from 'sonner';
import { DatePicker } from '../../Components/Common/DatePicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faCheck } from '@fortawesome/free-solid-svg-icons';
import { saveConnectionsBulk } from './ConnectionAPI';
import { getUsersArr } from '../../Store/Reducers';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../../Components/UI/button';
import { Card, CardContent } from '../../Components/UI/card';
import { setGlobalLoading } from '../../Store/Actions';
import { format } from "date-fns";
import { useVirtualizer } from '@tanstack/react-virtual';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../Components/UI/tooltip';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '../../Components/UI/dialog';
import { Check, X } from 'lucide-react';
import { Checkbox } from '../../Components/UI/checkbox';
import { Input } from '../../Components/UI/input';
import { Textarea } from '../../Components/UI/textarea';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../../Components/UI/select';
import { cn } from '../../Lib/Cn';
import { isLive, pluralize, normalizeDateFormat } from '../../Utils/Utilities';
import MobileInput from '../../Components/Common/MobileInput';
import { checkIfInqOnlyMode } from '../../Store/Reducers';

const TableCell = React.memo(({
    column,
    row,
    rowIndex,
    fieldIndex,
    hasError,
    currentError,
    errorMessage,
    renderInputField
}) => {
    const isCurrentErrorCell = currentError.rowIndex === rowIndex && currentError.fieldIndex === fieldIndex;
    return (
        <td
            key={`${column.field}-${rowIndex}`}
            className={`px-4 py-3 align-top ${isCurrentErrorCell ? "bg-red-50" : ""} `}
            style={{ minWidth: column.maxWidth, maxWidth: column.maxWidth }}
        >
            {renderInputField(column, row, rowIndex, fieldIndex)}
            {hasError && errorMessage && (
                <p className="text-sahi-red text-xs whitespace-normal break-words mt-1">
                    {errorMessage}
                </p>
            )}
        </td>
    );
}, (prevProps, nextProps) => {
    let cellValueChanged = prevProps.row[prevProps.column.field] !== nextProps.row[nextProps.column.field];
    if (prevProps.column.field == "Mobile") {
        cellValueChanged = true
    }
    const errorStateChanged = prevProps.hasError !== nextProps.hasError;
    const errorMessageChanged = prevProps.errorMessage !== nextProps.errorMessage;

    // Check if this cell's current error state changed
    const prevIsCurrentError = prevProps.currentError.rowIndex === prevProps.rowIndex &&
        prevProps.currentError.fieldIndex === prevProps.fieldIndex;
    const nextIsCurrentError = nextProps.currentError.rowIndex === nextProps.rowIndex &&
        nextProps.currentError.fieldIndex === nextProps.fieldIndex;
    const currentErrorStateChanged = prevIsCurrentError !== nextIsCurrentError;

    // Return true if nothing changed (don't re-render)
    return !cellValueChanged && !errorStateChanged && !errorMessageChanged && !currentErrorStateChanged;
});

// Optimized table row component
const TableRow = React.memo(({
    row,
    rowIndex,
    columns,
    errorCells,
    cellErrors,
    currentError,
    getCellKey,
    renderInputField
}) => {
    return (
        <tr id={`row-${rowIndex}`} className=" hover:bg-gray-50">
            {columns.map((col, fieldIndex) => {
                const cellKey = getCellKey(rowIndex, fieldIndex);
                const hasError = errorCells.has(cellKey);
                const errorMessage = cellErrors.get(cellKey);

                return (
                    <TableCell
                        key={`${col.field}-${rowIndex}`}
                        column={col}
                        row={row}
                        rowIndex={rowIndex}
                        fieldIndex={fieldIndex}
                        hasError={hasError}
                        errorMessage={errorMessage}
                        currentError={currentError}
                        renderInputField={renderInputField}
                    />
                );
            })}
        </tr>
    );
}, (prevProps, nextProps) => {
    const rowIndexChanged = prevProps.rowIndex !== nextProps.rowIndex;
    const columnsChanged = prevProps.columns.length !== nextProps.columns.length;

    const renderFunctionChanged = prevProps.renderInputField !== nextProps.renderInputField;

    return !rowIndexChanged && !columnsChanged && !renderFunctionChanged;
});

// Virtualized Table Component with Scrolling Header
function VirtualizedTable({
    data,
    columns,
    onInputChange,
    errorCells,
    cellErrors,
    currentError,
    getCellKey,
    renderInputField
}) {
    const parentRef = useRef();
    const headerRef = useRef();
    const [containerHeight, setContainerHeight] = useState(0);

    // Track container height for proper virtualization
    useEffect(() => {
        const updateHeight = () => {
            if (parentRef.current) {
                const height = parentRef.current.clientHeight;
                setContainerHeight(height);
            }
        };

        updateHeight();
        window.addEventListener('resize', updateHeight);

        return () => {
            window.removeEventListener('resize', updateHeight);
        };
    }, []);

    // Sync horizontal scroll between header and data rows
    useEffect(() => {
        const handleScroll = (e) => {
            if (headerRef.current) {
                headerRef.current.scrollLeft = e.target.scrollLeft;
            }
        };

        const scrollContainer = parentRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
            return () => scrollContainer.removeEventListener('scroll', handleScroll);
        }
    }, []);

    const rowVirtualizer = useVirtualizer({
        count: data.length + 1, // +1 for header row
        getScrollElement: () => parentRef.current,
        estimateSize: (index) => index === 0 ? 42 : 140,
        overscan: 10,
    });

    return (
        <div className="w-full h-full flex flex-col overflow-hidden">
            {/* Sticky Header with Horizontal Scroll Sync */}
            <div ref={headerRef} className="w-full bg-white shadow z-20 sticky top-0 overflow-x-auto scrollbar-hide">
                <table className="table-auto border-collapse w-full">
                    <thead>
                        <tr className="bg-white">
                            {columns.map((col, idx) => (
                                <th
                                    key={idx}
                                    className="px-4 py-2 text-left font-semibold border-b bg-white"
                                    style={{ minWidth: col.maxWidth, maxWidth: col.maxWidth }}
                                >
                                    {col.display}
                                </th>
                            ))}
                        </tr>
                    </thead>
                </table>
            </div>

            {/* Scrollable Data Rows */}
            <div
                ref={parentRef}
                className="flex-1 w-full overflow-y-scroll overflow-x-auto"
                style={{ minHeight: 0 }}
            >
                <div
                    style={{
                        height: `${rowVirtualizer.getTotalSize() - 42}px`,
                        width: '100%',
                        position: 'relative',
                    }}
                >
                    {(() => {
                        const virtualItems = rowVirtualizer.getVirtualItems();
                        return virtualItems.filter(item => item.index > 0).map((virtualItem) => (
                            <div
                                key={virtualItem.key}
                                className='w-full'
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: `${virtualItem.size}px`,
                                    transform: `translateY(${virtualItem.start - 42}px)`,
                                }}
                            >
                                <table className="table-auto border-collapse w-full">
                                    <tbody>
                                        <TableRow
                                            row={data[virtualItem.index - 1]}
                                            rowIndex={virtualItem.index - 1}
                                            columns={columns}
                                            onInputChange={onInputChange}
                                            errorCells={errorCells}
                                            cellErrors={cellErrors}
                                            currentError={currentError}
                                            getCellKey={getCellKey}
                                            renderInputField={renderInputField}
                                        />
                                    </tbody>
                                </table>
                            </div>
                        ));
                    })()}
                </div>
            </div>
        </div>
    );
}

let checkBoxCss = "h-5 w-5 data-[state=checked]:bg-sahi-primary-color data-[state=checked]:border-sahi-primary-color data-[state=checked]:text-white  transition-colors"
let erroNavBtnCss = "button-config text-base  flex items-center py-[6px] px-[10px]"

// Configuration constants for validation and batch processing
const connConst = {
    CHUNK_SIZE: 150,
    VALIDATION_BATCH_SIZE: 150
};

const CorrectExcel = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate();
    const location = useLocation();
    const { excelData: initialExcelData, errors: initialErrors, connectionData, loading, error } = location.state || { excelData: [], errors: [], connectionData: [], loading: false, error: null };
    const [apiError, setApiError] = useState(null);
    const [editableData, setEditableData] = useState([]);
    const [errorCells, setErrorCells] = useState(new Set()); // Tracks individual error cells
    const [cellErrors, setCellErrors] = useState(new Map()); // Map to store error messages for each cell
    const [currentError, setCurrentError] = useState({ rowIndex: -1, fieldIndex: -1 });
    const [saveDialog, setSaveDialog] = useState(false);
    const [ignoreErrors, setIngoreErrors] = useState(false)
    const [validationProgress, setValidationProgress] = useState(0);
    const [isValidating, setIsValidating] = useState(false);
    const validationAbortRef = useRef(false);
    const users = useSelector(getUsersArr);
    const isInqOnlyMode = useSelector(checkIfInqOnlyMode);
    const inputRefs = useRef([]);
    const [changedCells, setChangedCells] = useState(new Set());
    // Memoize static data to prevent unnecessary re-renders
    const memoizedCountries = useMemo(() => countries, []);
    const memoizedStates = useMemo(() => states, []);
    const regions = useSelector((state) => state.masterData.regions);

    const columns = [
        { field: 'No', maxWidth: '50px', type: 'text', display: 'No' },
        { field: 'BizName', maxWidth: '180px', type: 'text', display: <>Company</> },
        { field: 'ContactName', maxWidth: '180px', type: 'text', display: <>Contact Name</> },
        { field: 'Mobile', maxWidth: '200px', type: 'mobile', display: <>Mobile</> }, // Updated width and type
        { field: 'Email', maxWidth: '200px', type: 'email', display: 'Email' },
        { field: 'ConnType', maxWidth: '205px', type: 'select', display: <>Connection Type <span className="text-red-500">*</span></> },
        { field: 'Source', maxWidth: '150px', type: 'text', display: 'Source' },
        { field: 'Country', maxWidth: '170px', type: 'select', options: countries.map((c) => c.Country), display: 'Country' },
        { field: 'State', maxWidth: '150px', type: 'dynamic', display: 'State' },
        { field: 'City', maxWidth: '150px', type: 'text', display: 'City' },
        { field: 'GSTIN', maxWidth: '190px', type: 'text', display: 'GSTIN' },
        { field: 'POCUserID', maxWidth: '150px', type: 'select', options: users.map((u) => u.name), display: 'In-charge' },
        { field: 'ReceivedOnDT', maxWidth: '180px', type: 'date', display: 'Received on' },
        { field: 'Notes', maxWidth: '200px', type: 'textarea', display: 'Notes' },
        { field: 'LastRecordDT', maxWidth: '180px', type: 'date', display: 'Last Record on' },
        { field: 'LastRecord', maxWidth: '200px', type: 'textarea', display: 'Last Record Notes' },
        { field: 'NextTaskDT', maxWidth: '180px', type: 'date', display: 'Next Task on' },
        { field: 'NextTask', maxWidth: '200px', type: 'textarea', display: 'Next Task Notes' },
        { field: 'Tags', maxWidth: '200px', type: 'text', display: 'Tags' },
        { field: 'Potential', maxWidth: '120px', type: 'number', display: 'Potential' },
        { field: 'Segment', maxWidth: '170px', type: 'text', display: 'Category' },
        ...(regions.length > 0
            ? [{ field: 'RegionID', maxWidth: '170px', type: 'select', options: regions.map((rgn) => rgn.Rgn), display: 'Region', },]
            : []), ,
    ];

    const getCellKey = useCallback((rowIndex, fieldIndex) => `${rowIndex},${fieldIndex}`, []);

    function validateDateField(dateStr, fieldName, options = {}) {
        const { allowFuture = true, allowPast = true, isRequired = true } = options;

        if (!isRequired && !dateStr?.trim()) {
            return null;
        }

        if (isRequired && !dateStr?.trim()) {
            return `Please provide a ${fieldName}.`;
        }

        const trimmed = dateStr.trim();
        const dateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?$/;

        if (!dateRegex.test(trimmed) || isNaN(Date.parse(trimmed))) {
            return `Invalid date format`;
        }

        const date = new Date(trimmed);
        const today = new Date();
        date.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        if (!allowFuture && date > today) return `${fieldName} cannot be in the future.`;
        if (!allowPast && date < today) return `${fieldName} cannot be in the past.`;

        return null; // no error
    }

    // Update your useEffect for initializing data:
    // Single cell validation - wrapped in useCallback to prevent infinite loops
    const validateSingleCell = useCallback((data, rowIndex, fieldIndex) => {
        const row = data[rowIndex];
        const field = columns[fieldIndex].field;
        const cellKey = getCellKey(rowIndex, fieldIndex);
        let errorMessage = null;

        switch (field) {
            case 'BizName': {
                const biz = (row.BizName || "").toString().trim();
                const contact = (row.ContactName || "").toString().trim();

                if (!biz && !contact) {
                    errorMessage = 'Either company or contact name is required.';
                } else if (biz && biz.length > 100) {
                    errorMessage = 'Please enter a shorter name for the company.';
                }
                break;
            }

            case 'ContactName': {
                const biz = (row.BizName || "").toString().trim();
                const contact = (row.ContactName || "").toString().trim();

                if (!contact && !biz) {
                    errorMessage = "Either company or contact name is required.";
                } else if (contact && contact.length > 100) {
                    errorMessage = "Please enter shorter text for contact person's name.";
                }
                break;
            }

            case 'Mobile':
                if (!row.Mobile) {
                    errorMessage = 'Mobile number is required.';
                }
                // Check if CC (Country Code) is missing or 0
                else if (!row.CC || row.CC === 0) {
                    errorMessage = 'Please select a country for the mobile number.';
                }
                // Must be only digits
                else if (!/^\d+$/.test(row.Mobile)) {
                    errorMessage = 'Mobile must contain digits only.';
                }
                // No negative or leading zeros (except "0")
                else if (/^0\d+/.test(row.Mobile)) {
                    errorMessage = 'Mobile number cannot start with 0.';
                }
                else if (row.CountryID === 1 && !/^\d{10}$/.test(row.Mobile)) {
                    errorMessage = 'Mobile must be exactly 10 digits for India.';
                }
                else if (row.CountryID !== 1 && !/^\d{7,15}$/.test(row.Mobile)) {
                    errorMessage = 'Mobile must be 7 to 15 digits for non-India numbers.';
                }
                else if (/\s/.test(row.Mobile)) {
                    errorMessage = 'Mobile cannot contain spaces.';
                }
                else if (connectionData && !loading && !error) {
                    if (connectionData.some((conn) => conn.Mobile === row.Mobile)) {
                        errorMessage = 'Another user with this mobile number is already present in your network.';
                    }
                    // Check for duplicates in current data (excluding current row)
                    if (data.some((otherRow, otherIndex) => otherIndex !== rowIndex && otherRow.Mobile === row.Mobile)) {
                        errorMessage = 'Duplicate mobile number in the data.';
                    }
                }
                break;

            case 'Email':
                if (row.Email && (row.Email || "").toString().trim() !== '') {
                    const emailStr = (row.Email || "").toString();
                    if (/\s/.test(emailStr)) {
                        errorMessage = 'Email cannot contain spaces.';
                    } else if (emailStr.length > 50) {
                        errorMessage = 'Please enter a shorter email address.';
                    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr)) {
                        errorMessage = 'Invalid email format.';
                    } else if (connectionData.some((conn) => conn.Email === row.Email)) {
                        errorMessage = 'Email already exists.';
                    } else if (data.some((otherRow, otherIndex) => otherIndex !== rowIndex && otherRow.Email === row.Email)) {
                        errorMessage = 'Duplicate email in the data.';
                    }
                }
                break;

            case 'ConnType':
                if (!row.ConnType || (row.ConnType || "").toString().trim() === '') {
                    errorMessage = 'Connection type is required.';
                } else if (!(row.ConnType || "").toString().includes('C') && !(row.ConnType || "").toString().includes('S') && !(row.ConnType || "").toString().includes('P')) {
                    errorMessage = 'Please select a valid connection type.';
                }
                break;

            case 'Source':
                if (row.Source && (row.Source || "").toString().trim() !== '' && (row.Source || "").toString().length > 50) {
                    errorMessage = 'Please enter a shorter source name.';
                }
                break;

            case 'Country':
                if (row.Country && (row.Country || "").toString().trim() !== '') {
                    const countryStr = (row.Country || "").toString();
                    const country = countries.find((c) => c.Country.toLowerCase() === countryStr.toLowerCase());
                    if (!country) {
                        errorMessage = 'Country must be a valid country from the list.';
                    }
                }
                break;

            case 'State':
                if (row.State && (row.State || "").toString().trim() !== '') {
                    const stateStr = (row.State || "").toString();
                    if (stateStr.length > 50) {
                        errorMessage = 'Please enter a shorter state name.';
                    } else if (row.CountryID === 1) {
                        const selectedState = states.find((s) => s.State.toLowerCase() === stateStr.toLowerCase() && s.CountryID === 1);
                        if (!selectedState) {
                            errorMessage = 'State must be a valid Indian state.';
                        }
                    }
                }
                break;

            case 'City':
                if (row.City && (row.City || "").toString().trim() !== '' && (row.City || "").toString().length > 50) {
                    errorMessage = 'Please enter a shorter city name.';
                }
                break;

            case 'GSTIN':
                if (row.GSTIN && (row.GSTIN || "").toString().trim() !== '') {
                    const gstin = (row.GSTIN || "").toString().trim().toUpperCase();
                    if (row.CountryID === 1) {
                        if (gstin.length !== 15) {
                            errorMessage = 'Please enter a valid GSTIN number (15 characters).';
                        } else {
                            const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
                            if (!gstinRegex.test(gstin)) {
                                errorMessage = 'GSTIN format is invalid.';
                            } else if (row.State) {
                                const selectedState = states.find((s) => s.State.toLowerCase() === row.State.toLowerCase() && s.CountryID === 1);
                                if (selectedState && !gstin.startsWith(selectedState.GSTPrefix)) {
                                    errorMessage = 'GSTIN must start with the correct state code.';
                                }
                            }
                        }
                    }
                }
                break;

            case 'Notes':
                if (row.Notes && (row.Notes || "").toString().trim() !== '' && (row.Notes || "").toString().length > 1000) {
                    errorMessage = 'Please enter shorter text for notes.';
                }
                break;

            case "LastRecordDT":
                errorMessage = validateDateField(
                    row.LastRecordDT,
                    "Last record date",
                    {
                        allowFuture: false,
                        isRequired: !!(row.LastRecord && (row.LastRecord || "").toString().trim())
                    }
                );
                break;

            case 'LastRecord':
                if (row.LastRecordDT && (!row.LastRecord || (row.LastRecord || "").toString().trim() === '')) {
                    errorMessage = 'Please provide the last record details.';
                } else if (row.LastRecord && (row.LastRecord || "").toString().trim() !== '' && (row.LastRecord || "").toString().length > 1000) {
                    errorMessage = 'Please enter shorter text for last record.';
                }
                break;

            case "NextTaskDT":
                errorMessage = validateDateField(
                    row.NextTaskDT,
                    "Next task date",
                    {
                        allowPast: false,
                        isRequired: !!(row.NextTask && (row.NextTask || "").toString().trim())
                    }
                );
                break;

            case 'NextTask':
                if (row.NextTaskDT && (!row.NextTask || (row.NextTask || "").toString().trim() === '')) {
                    errorMessage = 'Please provide a next task.';
                } else if (row.NextTask && (row.NextTask || "").toString().trim() !== '' && (row.NextTask || "").toString().length > 1000) {
                    errorMessage = 'Please enter shorter text for next task.';
                }
                break;

            case 'Tags':
                if (row.Tags && (row.Tags || "").toString().trim() !== '' && (row.Tags || "").toString().length > 200) {
                    errorMessage = 'Please enter shorter text for tags (max 200 characters).';
                }
                break;

            case 'Segment':
                if (row.Segment && (row.Segment || "").toString().trim() !== '' && (row.Segment || "").toString().length > 50) {
                    errorMessage = 'Please enter a shorter segment.';
                }
                break;

            case 'POCUserID':
                if (row.POCUserID && !users.some((u) => String(u.userID) === row.POCUserID)) {
                    errorMessage = 'Select a valid in-charge from the list.';
                }
                break;

            case 'Potential':
                if (row.Potential) {
                    // check if numeric
                    if (isNaN(row.Potential)) {
                        errorMessage = 'Potential must be a valid number.';
                    } else if (Number(row.Potential) < 0) {
                        errorMessage = 'Potential cannot be negative.';
                    } else if (row.Potential.toString().length > 50) {
                        errorMessage = 'Potential must be 50 characters or fewer.';
                    }
                }
                break;

            default:
                errorMessage = null;
                break;
        }

        return { cellKey, errorMessage };
    }, [columns, users, validateDateField, connectionData, loading, error, memoizedCountries, memoizedStates, getCellKey]);

    // Validate data in chunks to prevent UI blocking
    const validateDataInChunks = useCallback(async (data) => {
        setIsValidating(true);
        validationAbortRef.current = false;
        const batchSize = connConst.VALIDATION_BATCH_SIZE;
        let allErrorCells = new Set();
        let allCellErrors = new Map();

        try {
            // Process validation in batches
            for (let i = 0; i < data.length; i += batchSize) {
                if (validationAbortRef.current) break;

                const endIdx = Math.min(i + batchSize, data.length);

                // Validate this batch
                for (let rowIndex = i; rowIndex < endIdx; rowIndex++) {
                    if (validationAbortRef.current) break;

                    columns.forEach((col, fieldIndex) => {
                        if (col.field !== 'No') {
                            const { cellKey, errorMessage } = validateSingleCell(data, rowIndex, fieldIndex);
                            if (errorMessage) {
                                allErrorCells.add(cellKey);
                                allCellErrors.set(cellKey, errorMessage);
                            }
                        }
                    });
                }

                // Check for duplicate mobiles/emails in current batch
                const mobileFieldIndex = columns.findIndex(col => col.field === 'Mobile');
                const emailFieldIndex = columns.findIndex(col => col.field === 'Email');

                if (mobileFieldIndex !== -1) {
                    const valueToRows = new Map();
                    for (let idx = i; idx < endIdx; idx++) {
                        const value = data[idx].Mobile?.trim();
                        if (value) {
                            if (!valueToRows.has(value)) {
                                valueToRows.set(value, []);
                            }
                            valueToRows.get(value).push(idx);
                        }
                    }

                    valueToRows.forEach((rowIndices) => {
                        if (rowIndices.length > 1) {
                            rowIndices.forEach(rowIdx => {
                                const cellKey = getCellKey(rowIdx, mobileFieldIndex);
                                allErrorCells.add(cellKey);
                                allCellErrors.set(cellKey, 'Duplicate mobile number in the data.');
                            });
                        }
                    });
                }

                if (emailFieldIndex !== -1) {
                    const valueToRows = new Map();
                    for (let idx = i; idx < endIdx; idx++) {
                        const value = data[idx].Email?.trim();
                        if (value) {
                            if (!valueToRows.has(value)) {
                                valueToRows.set(value, []);
                            }
                            valueToRows.get(value).push(idx);
                        }
                    }

                    valueToRows.forEach((rowIndices) => {
                        if (rowIndices.length > 1) {
                            rowIndices.forEach(rowIdx => {
                                const cellKey = getCellKey(rowIdx, emailFieldIndex);
                                allErrorCells.add(cellKey);
                                allCellErrors.set(cellKey, 'Duplicate email in the data.');
                            });
                        }
                    });
                }

                // Update progress
                const progress = Math.round((endIdx / data.length) * 100);
                setValidationProgress(progress);

                // Yield to main thread to prevent UI blocking
                await new Promise(resolve => setTimeout(resolve, 0));
            }

            // Set final error states
            setErrorCells(allErrorCells);
            setCellErrors(allCellErrors);

            // Set current error
            if (allErrorCells.size > 0) {
                const firstError = Array.from(allErrorCells)[0];
                const [rowIndex, fieldIndex] = firstError.split(',').map(Number);
                setCurrentError({ rowIndex, fieldIndex });
            } else {
                setCurrentError({ rowIndex: -1, fieldIndex: -1 });
            }
        } finally {
            setIsValidating(false);
            setValidationProgress(0);
        }
    }, [columns, validateSingleCell, getCellKey]);

    useEffect(() => {
        const initializeData = async () => {
            dispatch(setGlobalLoading(true));

            try {
                if (initialExcelData?.length > 0) {
                    const sanitizedData = initialExcelData.map(row => ({
                        ...row,
                        LastRecordDT: row.LastRecordDT ? normalizeDateFormat(row.LastRecordDT) : row.LastRecordDT,
                        NextTaskDT: row.NextTaskDT ? normalizeDateFormat(row.NextTaskDT) : row.NextTaskDT,
                        // Ensure default values for country-related fields
                        CountryID: row.CountryID || 1, // Default to India
                        CC: row.CC || 91, // Default to India's country code
                        Country: row.Country || (row.CountryID === 1 || !row.CountryID ? 'India' : ''),
                        ReceivedOnDT: row.ReceivedOnDT ? normalizeDateFormat(row.ReceivedOnDT) : new Date().toISOString(),
                    }));

                    setEditableData(sanitizedData);
                    inputRefs.current = Array(sanitizedData.length).fill()
                        .map(() => Array(20).fill().map(() => React.createRef()));

                    // Use chunked validation for large datasets to prevent UI blocking
                    await validateDataInChunks(sanitizedData);
                }
            } catch (error) {
                console.error('Error initializing data:', error);
            } finally {
                dispatch(setGlobalLoading(false));
            }
        };

        initializeData();
    }, [initialExcelData, dispatch]);

    const getNextCell = useCallback((currentRowIndex, currentFieldIndex, direction = 'forward') => {
        const totalRows = editableData.length;
        const totalFields = columns.length;
        let nextRowIndex = currentRowIndex;
        let nextFieldIndex = currentFieldIndex;

        if (direction === 'forward') {
            nextFieldIndex++;
            if (nextFieldIndex >= totalFields) {
                nextFieldIndex = 1; // Skip 'No' column (index 0)
                nextRowIndex++;
                if (nextRowIndex >= totalRows) {
                    nextRowIndex = 0;
                }
            }
        } else {
            nextFieldIndex--;
            if (nextFieldIndex < 1) { // Skip 'No' column (index 0)
                nextFieldIndex = totalFields - 1;
                nextRowIndex--;
                if (nextRowIndex < 0) {
                    nextRowIndex = totalRows - 1;
                }
            }
        }

        // Skip 'No' column
        if (nextFieldIndex === 0) {
            return getNextCell(nextRowIndex, nextFieldIndex, direction);
        }

        return { rowIndex: nextRowIndex, fieldIndex: nextFieldIndex };
    }, [editableData.length]);

    const [currentFocusedCell, setCurrentFocusedCell] = useState({ rowIndex: -1, fieldIndex: -1 });
    const [previousCell, setPreviousCell] = useState({ rowIndex: -1, fieldIndex: -1 });

    // Update the focusCell function to track current and previous positions
    const focusCell = useCallback((rowIndex, fieldIndex) => {
        const field = columns[fieldIndex]?.field;
        let targetRef = inputRefs.current[rowIndex]?.[fieldIndex]?.current;

        if (field === "LastRecordDT" || field === "NextTaskDT") {
            if (!targetRef) {
                const dateContainer = document.querySelector(`#date-${rowIndex}-${fieldIndex}`);
                if (dateContainer) {
                    const button = dateContainer.querySelector('button');
                    if (button) {
                        targetRef = button;
                    }
                }
            }
        } else if (field === "Mobile") {
            // For mobile input, target the actual input field within the component
            if (!targetRef) {
                const mobileContainer = document.querySelector(`#mobile-${rowIndex}-${fieldIndex}`);
                if (mobileContainer) {
                    const input = mobileContainer.querySelector('input');
                    if (input) {
                        targetRef = input;
                    }
                }
            }
        } else if (field === "Country" || field === "State" || field === "POCUserID") {
            if (targetRef) {
                const selectTrigger = targetRef.querySelector('[role="combobox"]') ||
                    targetRef.querySelector('button[role="combobox"]') ||
                    targetRef.querySelector('button') ||
                    targetRef;

                if (selectTrigger && selectTrigger !== targetRef) {
                    targetRef = selectTrigger;
                }
            }
        }

        if (targetRef) {
            setPreviousCell({
                rowIndex: currentFocusedCell.rowIndex,
                fieldIndex: currentFocusedCell.fieldIndex
            });

            setCurrentFocusedCell({ rowIndex, fieldIndex });

            const doFocus = () => {
                try {
                    if (targetRef && typeof targetRef.focus === 'function') {
                        targetRef.focus();

                        if ((targetRef.type === "text" || targetRef.tagName === "TEXTAREA") &&
                            typeof targetRef.select === 'function') {
                            targetRef.select();
                        }
                    }
                } catch (error) {
                    console.error('Focus error:', error);
                }
            };

            doFocus();
            requestAnimationFrame(() => {
                if (document.activeElement !== targetRef) {
                    doFocus();
                }
            });

            return true;
        }
        return false;
    }, [currentFocusedCell, columns]);

    const getSortedErrorCells = useCallback(() => {
        return Array.from(errorCells)
            .map(cellKey => {
                const [rowIndex, fieldIndex] = cellKey.split(',').map(Number);
                return { rowIndex, fieldIndex, cellKey };
            })
            .sort((a, b) => {
                if (a.rowIndex !== b.rowIndex) {
                    return a.rowIndex - b.rowIndex;
                }
                return a.fieldIndex - b.fieldIndex;
            });
    }, [errorCells]);

    const findNextErrorAfterPosition = useCallback((currentRowIndex, currentFieldIndex) => {
        const sortedErrors = getSortedErrorCells();
        if (sortedErrors.length === 0) return null;

        const nextError = sortedErrors.find(error => {
            if (error.rowIndex > currentRowIndex) return true;
            if (error.rowIndex === currentRowIndex && error.fieldIndex > currentFieldIndex) return true;
            return false;
        });

        // If no error found after current position, wrap to first error
        return nextError || sortedErrors[0];
    }, [getSortedErrorCells]);

    const findPreviousErrorBeforePosition = useCallback((currentRowIndex, currentFieldIndex) => {
        const sortedErrors = getSortedErrorCells();
        if (sortedErrors.length === 0) return null;

        // Find the last error that comes before the current position
        let previousError = null;
        for (let i = sortedErrors.length - 1; i >= 0; i--) {
            const error = sortedErrors[i];
            // First compare by row
            if (error.rowIndex < currentRowIndex) {
                previousError = error;
                break;
            }
            // If same row, compare by field index
            if (error.rowIndex === currentRowIndex && error.fieldIndex < currentFieldIndex) {
                previousError = error;
                break;
            }
        }

        // If no error found before current position, wrap to last error
        return previousError || sortedErrors[sortedErrors.length - 1];
    }, [getSortedErrorCells]);

    // Saving validated data to the server
    const handleAddClick = async () => {
        // If ignoring errors, get only Valid rows
        const dataToSave = ignoreErrors ? getErrorFreeRows() : editableData;

        if (!ignoreErrors && errorCells.size > 0) {
            toast.error(`Please fix ${errorCells.size} error(s) before saving.`);
            return;
        }

        if (ignoreErrors && dataToSave.length === 0) {
            toast.error('No valid rows to save. All rows have errors.');
            return;
        }

        setApiError(null);

        try {
            dispatch(setGlobalLoading(true))
            const formatDate = (dateString) => {
                if (!dateString) return '';
                try {
                    const date = new Date(dateString);
                    if (isNaN(date.getTime())) return '';

                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');

                    return `${year}-${month}-${day}T00:00:00`;
                } catch {
                    return '';
                }
            };
            const connParams = dataToSave.map((row) => ({
                ConnID: parseInt(row.ConnID) || 0,
                BizName: row.BizName || '',
                ContactName: row.ContactName || '',
                Contacts: row.Contacts || '[]',
                Mobile: row.Mobile ? String(row.Mobile).trim() : '',
                // FIX: Ensure CC is properly set and never 0
                CC: row.CC && row.CC !== 0 ? Number(row.CC) :
                    (row.CountryID === 1 ? 91 :
                        row.CountryID ? memoizedCountries.find(c => c.CountryID === row.CountryID)?.CC || 91 : 91),
                Email: row.Email || '',
                POCUserID: Number(row.POCUserID) || 0,
                Source: row.Source || '',
                SinceDT: formatDate(row.ReceivedOnDT || new Date()),
                Notes: row.Notes || '',
                City: row.City || '',
                StateID: row.StateID !== undefined ? Number(row.StateID, 10) || 0 : 0,
                State: row.State || '',
                CountryID: row.CountryID !== undefined ? Number(row.CountryID, 10) || 1 : 1, // Default to 1 (India)
                Country: row.Country || '',
                GSTIN: row.GSTIN || '',
                LastRec: row.LastRecord || '',
                LastRecDT: formatDate(row.LastRecordDT) || null,
                NextTask: row.NextTask || '',
                NextTaskDT: formatDate(row.NextTaskDT) || null,
                NextTaskTyp: row.NextTask ? (String(row.NextTaskTyp || '').replace(/^-$/, '') || 'Internal') : '',
                Tags: row.Tags || '',
                Potential: Number(row.Potential) || 0,
                Segment: row.Segment == 1 ? '100%' : row.Segment || '',
                RegionID: row.RegionID || 0,
                Type: row.ConnType || '',
                Fields: row.Fields || '[]',
                ...(isLive() ? { ConnType: row.ConnType || '' } : {}),
                IsError: row.IsError || 0,
                ErrMsg: row.ErrMsg || '',
            }));

            const params = {
                connParams,
                src: 'Excel',
            }
            const result = await saveConnectionsBulk(params);

            if (!result.success) {
                throw new Error(result.message || 'Contact Import failed');
            }

            closeDialog()
            const successMessage = ignoreErrors && dataToSave.length < editableData.length
                ? `${dataToSave.length} contacts imported. ${editableData.length - dataToSave.length} rows with errors were skipped.`
                : 'Contacts imported.';

            toast.success(successMessage, {
                duration: 3000,
                position: 'bottom-right',
            });

            setTimeout(() => {
                navigate('/connections');
            }, 3000);

        } catch (err) {
            setApiError(err.message);
            setSaveDialog(false);
        } finally {
            dispatch(setGlobalLoading(false))
        }
    };

    // Get dependent cells that need revalidation when a cell changes
    const getDependentCells = (rowIndex, fieldIndex, field) => {
        const dependents = [];

        if (field === 'Country') {
            const stateFieldIndex = columns.findIndex(col => col.field === 'State');
            const gstinFieldIndex = columns.findIndex(col => col.field === 'GSTIN');
            if (stateFieldIndex !== -1) dependents.push({ rowIndex, fieldIndex: stateFieldIndex });
            if (gstinFieldIndex !== -1) dependents.push({ rowIndex, fieldIndex: gstinFieldIndex });
        } else if (field === 'State') {
            const gstinFieldIndex = columns.findIndex(col => col.field === 'GSTIN');
            if (gstinFieldIndex !== -1) dependents.push({ rowIndex, fieldIndex: gstinFieldIndex });
        } else if (field === 'LastRecordDT') {
            const lastRecFieldIndex = columns.findIndex(col => col.field === 'LastRecord');
            if (lastRecFieldIndex !== -1) dependents.push({ rowIndex, fieldIndex: lastRecFieldIndex });
        } else if (field === 'LastRecord') {
            // ADD THIS: When LastRecord changes, revalidate LastRecordDT
            const lastRecordDTFieldIndex = columns.findIndex(col => col.field === 'LastRecordDT');
            if (lastRecordDTFieldIndex !== -1) dependents.push({ rowIndex, fieldIndex: lastRecordDTFieldIndex });
        } else if (field === 'NextTaskDT') {
            const nextTaskFieldIndex = columns.findIndex(col => col.field === 'NextTask');
            if (nextTaskFieldIndex !== -1) dependents.push({ rowIndex, fieldIndex: nextTaskFieldIndex });
        } else if (field === 'NextTask') {
            // ADD THIS: When NextTask changes, revalidate NextTaskDT
            const nextTaskDTFieldIndex = columns.findIndex(col => col.field === 'NextTaskDT');
            if (nextTaskDTFieldIndex !== -1) dependents.push({ rowIndex, fieldIndex: nextTaskDTFieldIndex });
        } else if (field === 'BizName') {
            const contactNameIndex = columns.findIndex(col => col.field === 'ContactName');
            if (contactNameIndex !== -1) dependents.push({ rowIndex, fieldIndex: contactNameIndex });
        } else if (field === 'ContactName') {
            const bizNameIndex = columns.findIndex(col => col.field === 'BizName');
            if (bizNameIndex !== -1) dependents.push({ rowIndex, fieldIndex: bizNameIndex });
        }

        return dependents;
    };

    // Optimized validation function
    const validateData = (data, cellCoordinate = null) => {
        const newErrorCells = new Set(errorCells);
        const newCellErrors = new Map(cellErrors);

        if (cellCoordinate === null) {
            newErrorCells.clear();
            newCellErrors.clear();

            data.forEach((row, rowIndex) => {
                columns.forEach((col, fieldIndex) => {
                    if (col.field !== 'No') {
                        const { cellKey, errorMessage } = validateSingleCell(data, rowIndex, fieldIndex);
                        if (errorMessage) {
                            newErrorCells.add(cellKey);
                            newCellErrors.set(cellKey, errorMessage);
                        }
                    }
                });
            });
        } else {
            const { rowIndex, fieldIndex } = cellCoordinate;
            const field = columns[fieldIndex].field;

            const { cellKey, errorMessage } = validateSingleCell(data, rowIndex, fieldIndex);

            if (errorMessage) {
                newErrorCells.add(cellKey);
                newCellErrors.set(cellKey, errorMessage);
            } else {
                newErrorCells.delete(cellKey);
                newCellErrors.delete(cellKey);
            }

            // Validate dependent cells
            const dependentCells = getDependentCells(rowIndex, fieldIndex, field);
            dependentCells.forEach(({ rowIndex: depRowIndex, fieldIndex: depFieldIndex }) => {
                const { cellKey: depCellKey, errorMessage: depErrorMessage } = validateSingleCell(data, depRowIndex, depFieldIndex);

                if (depErrorMessage) {
                    newErrorCells.add(depCellKey);
                    newCellErrors.set(depCellKey, depErrorMessage);
                } else {
                    newErrorCells.delete(depCellKey);
                    newCellErrors.delete(depCellKey);
                }
            });

            // Handle duplicate validations for Mobile and Email across all data
            if (field === 'Mobile' || field === 'Email') {
                // Step 1: Clear all existing duplicate errors for this field type
                const duplicateMessage = field === 'Mobile' ? 'Duplicate mobile number in the data.' : 'Duplicate email in the data.';
                for (const cellKey of newErrorCells) {
                    if (newCellErrors.get(cellKey) === duplicateMessage) {
                        newErrorCells.delete(cellKey);
                        newCellErrors.delete(cellKey);
                    }
                }

                const fieldIndex = columns.findIndex(col => col.field === field);
                const valueToRows = new Map(); // Map value to list of row indices

                data.forEach((row, rowIdx) => {
                    const value = row[field]?.trim();
                    if (value) {
                        if (!valueToRows.has(value)) {
                            valueToRows.set(value, []);
                        }
                        valueToRows.get(value).push(rowIdx);
                    }
                });

                valueToRows.forEach((rowIndices, value) => {
                    if (rowIndices.length > 1) {
                        rowIndices.forEach(rowIdx => {
                            const cellKey = getCellKey(rowIdx, fieldIndex);
                            newErrorCells.add(cellKey);
                            newCellErrors.set(cellKey, duplicateMessage);
                        });
                    }
                });
            }
        }

        setErrorCells(newErrorCells);
        setCellErrors(newCellErrors);

        // Update current error if needed
        const errorCellsArray = Array.from(newErrorCells);
        if (errorCellsArray.length > 0 && (currentError.rowIndex === -1 || !newErrorCells.has(getCellKey(currentError.rowIndex, currentError.fieldIndex)))) {
            const firstErrorCell = errorCellsArray[0];
            const [rowIndex, fieldIndex] = firstErrorCell.split(',').map(Number);
            setCurrentError({ rowIndex, fieldIndex });
        } else if (errorCellsArray.length === 0) {
            setCurrentError({ rowIndex: -1, fieldIndex: -1 });
        }

        return { errorCells: newErrorCells, cellErrors: newCellErrors };
    };

    // Initial validation of all data
    const validateAllData = (data) => {
        validateData(data, null);
    };

    const handleInputChange = (index, fieldIndex, value) => {
        const inputRef = inputRefs.current[index]?.[fieldIndex]?.current;
        const cursorPosition = inputRef?.selectionStart || null;

        setEditableData((prevData) => {
            const currentValue = prevData[index][columns[fieldIndex].field];
            if (currentValue === value) return prevData;

            const newRow = { ...prevData[index] };
            const field = columns[fieldIndex].field;

            // Normalize date fields when user enters them
            if ((field === 'LastRecordDT' || field === 'NextTaskDT') && value) {
                newRow[field] = normalizeDateFormat(value);
            } else {
                newRow[field] = value || "";
            }

            if (field === "Country") {
                const country = memoizedCountries.find((c) => c.Country === value);
                newRow.CountryID = country ? country.CountryID : 0;
                newRow.CC = country ? country.CC : "";
                // Reset State and StateID when country changes
                newRow.State = "";
                newRow.StateID = 0;
            } else if (field === "State" && newRow.CountryID === 1) {
                const state = memoizedStates.find((s) => s.State === value && s.CountryID === 1);
                newRow.StateID = state ? state.StateID : 0;
            } else if (field === "POCUserID") {
                const user = users.find((u) => u.name === value);
                newRow.POCUserID = user ? String(user.userID) : "";
            } else if (field === "RegionID") {
                const region = regions.find((r) => r.Rgn === value);
                newRow.RegionID = region ? region.RID : 0;
            }
            const updatedData = [...prevData];
            updatedData[index] = newRow;
            return updatedData;
        });

        setChangedCells((prev) => {
            const newSet = new Set(prev);
            newSet.add(`${index},${fieldIndex}`);
            return newSet;
        });

        // Restore cursor position for text inputs
        if (inputRef?.type === "text" || inputRef?.tagName === "TEXTAREA") {
            setTimeout(() => {
                if (inputRef) {
                    inputRef.selectionStart = cursorPosition;
                    inputRef.selectionEnd = cursorPosition;
                }
            }, 0);
        }
    };
    // Optimized validation effect for changed cells
    const validationTimeoutRef = useRef(null);

    useEffect(() => {
        // Only run if there are pending changes
        if (changedCells.size > 0) {
            if (validationTimeoutRef.current) {
                clearTimeout(validationTimeoutRef.current);
            }

            validationTimeoutRef.current = setTimeout(() => {
                // Create a temporary copy of the cells to validate
                const cellsToValidate = Array.from(changedCells).map(cellKey => {
                    const [rowIndex, fieldIndex] = cellKey.split(',').map(Number);
                    return { rowIndex, fieldIndex };
                });

                // Clear the changed cells state
                setChangedCells(new Set());

                // Run validation only on the changed and dependent cells
                cellsToValidate.forEach(({ rowIndex, fieldIndex }) => {
                    validateData(editableData, { rowIndex, fieldIndex });
                });

            }, 200); // The debounce time can be adjusted
        }

        return () => {
            if (validationTimeoutRef.current) {
                clearTimeout(validationTimeoutRef.current);
            }
        };

        // The dependency array is now much simpler
    }, [changedCells, editableData]);


    //calucate errorfree rows
    const getErrorFreeRows = useCallback(() => {
        const errorFreeRows = [];
        const errorRowIndices = new Set();

        // Get all row indices that have errors
        errorCells.forEach(cellKey => {
            const [rowIndex] = cellKey.split(',').map(Number);
            errorRowIndices.add(rowIndex);
        });

        // Filter out rows that have errors
        editableData.forEach((row, index) => {
            if (!errorRowIndices.has(index)) {
                errorFreeRows.push({
                    ...row,
                    originalIndex: index // Keep track of original position
                });
            }
        });

        return errorFreeRows;
    }, [editableData, errorCells]);

    // Returns { rowIndex, fieldIndex } if found, otherwise null
    const findActiveElement = (activeElement, inputRefs) => {
        if (!activeElement || !inputRefs.current.length) return null;

        for (let rowIndex = 0; rowIndex < inputRefs.current.length; rowIndex++) {
            const row = inputRefs.current[rowIndex];
            for (let fieldIndex = 0; fieldIndex < row.length; fieldIndex++) {
                const ref = row[fieldIndex];
                if (ref?.current === activeElement || ref?.current?.contains?.(activeElement)) {
                    return { rowIndex, fieldIndex };
                }
            }
        }

        if (activeElement?.id?.startsWith('date-') || activeElement?.closest('[id^="date-"]')) {
            const dateElement = activeElement?.id?.startsWith('date-') ?
                activeElement : activeElement.closest('[id^="date-"]');

            if (dateElement?.id) {
                const match = dateElement.id.match(/date-(\d+)-(\d+)/);
                if (match) {
                    return { rowIndex: parseInt(match[1]), fieldIndex: parseInt(match[2]) };
                }
            }
        }

        // Check for mobile input field
        if (activeElement?.id?.startsWith('mobile-') || activeElement?.closest('[id^="mobile-"]')) {
            const mobileElement = activeElement?.id?.startsWith('mobile-') ?
                activeElement : activeElement.closest('[id^="mobile-"]');

            if (mobileElement?.id) {
                const match = mobileElement.id.match(/mobile-(\d+)-(\d+)/);
                if (match) {
                    return { rowIndex: parseInt(match[1]), fieldIndex: parseInt(match[2]) };
                }
            }
        }

        let element = activeElement;
        while (element && element.tagName !== 'TD') {
            element = element.parentElement;
            if (!element || element.tagName === 'BODY') break;
        }

        if (element?.tagName === 'TD') {
            const row = element.closest('tr[id^="row-"]');
            if (row?.id) {
                const rowMatch = row.id.match(/^row-(\d+)$/);
                if (rowMatch) {
                    const rowIndex = parseInt(rowMatch[1]);
                    const cells = Array.from(row.children);
                    const fieldIndex = cells.indexOf(element);
                    if (fieldIndex !== -1) {
                        return { rowIndex, fieldIndex };
                    }
                }
            }
        }

        return null;
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key !== "Tab") return;

            e.preventDefault();
            const activeElement = document.activeElement;
            const currentCell = findActiveElement(activeElement, inputRefs);

            if (currentCell) {
                const nextCell = getNextCell(
                    currentCell.rowIndex,
                    currentCell.fieldIndex,
                    e.shiftKey ? "backward" : "forward"
                );

                if (nextCell) {
                    focusCell(nextCell.rowIndex, nextCell.fieldIndex);
                }
            } else if (inputRefs.current.length > 0) {
                focusCell(0, 1); // Start at first editable column (skip 'No')
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [editableData.length, getNextCell, focusCell]);

    useEffect(() => {
        const handleFocusIn = (e) => {
            const activeElement = e.target;
            const currentCell = findActiveElement(activeElement, inputRefs);

            if (currentCell && (
                currentCell.rowIndex !== currentFocusedCell.rowIndex ||
                currentCell.fieldIndex !== currentFocusedCell.fieldIndex
            )) {
                // Store previous cell before updating current
                setPreviousCell({
                    rowIndex: currentFocusedCell.rowIndex,
                    fieldIndex: currentFocusedCell.fieldIndex
                });

                // Update current focused cell
                setCurrentFocusedCell({
                    rowIndex: currentCell.rowIndex,
                    fieldIndex: currentCell.fieldIndex
                });
                const cellKey = getCellKey(currentCell.rowIndex, currentCell.fieldIndex);
                const hasError = errorCells.has(cellKey);
                if (hasError) {
                    setCurrentError({ rowIndex: currentCell.rowIndex, fieldIndex: currentCell.fieldIndex })
                } else {
                    setCurrentError({ rowIndex: -1, fieldIndex: -1 })
                }
            }
        };

        document.addEventListener("focusin", handleFocusIn);
        return () => document.removeEventListener("focusin", handleFocusIn);
    }, [currentFocusedCell]);

    const getNextErrorButtonDisabled = () => {
        const sortedErrors = getSortedErrorCells();
        if (sortedErrors.length === 0) return true;

        const referenceRow = currentFocusedCell.rowIndex !== -1 ? currentFocusedCell.rowIndex : currentError.rowIndex;
        const referenceField = currentFocusedCell.fieldIndex !== -1 ? currentFocusedCell.fieldIndex : currentError.fieldIndex;

        if (referenceRow === -1 || referenceField === -1) return false;

        // If current position is exactly the last error, disable next
        const lastError = sortedErrors[sortedErrors.length - 1];
        if (referenceRow === lastError.rowIndex && referenceField === lastError.fieldIndex) return true;

        return false;
    };

    const DebugCellState = () => (
        <div className="text-xs text-gray-500 p-2 bg-gray-100 rounded">
            <div>Current Focused: ({currentFocusedCell.rowIndex}, {currentFocusedCell.fieldIndex})</div>
            <div>Previous Cell: ({previousCell.rowIndex}, {previousCell.fieldIndex})</div>
            <div>Current Error: ({currentError.rowIndex}, {currentError.fieldIndex})</div>
        </div>
    );

    const getPrevErrorButtonDisabled = () => {
        const sortedErrors = getSortedErrorCells();
        if (sortedErrors.length === 0) return true;

        const referenceRow = currentFocusedCell.rowIndex !== -1 ? currentFocusedCell.rowIndex : currentError.rowIndex;
        const referenceField = currentFocusedCell.fieldIndex !== -1 ? currentFocusedCell.fieldIndex : currentError.fieldIndex;

        if (referenceRow === -1 || referenceField === -1) return false;

        // If current position is exactly the first error, disable prev
        const firstError = sortedErrors[0];
        if (referenceRow === firstError.rowIndex && referenceField === firstError.fieldIndex) return true;

        return false;
    };
    // Navigating to the next error 
    const handleNextError = useCallback(() => {
        const sortedErrors = getSortedErrorCells();
        if (sortedErrors.length === 0) return;

        let nextError;

        // Use current focused cell position, fallback to current error
        const referenceRow = currentFocusedCell.rowIndex !== -1 ? currentFocusedCell.rowIndex : currentError.rowIndex;
        const referenceField = currentFocusedCell.fieldIndex !== -1 ? currentFocusedCell.fieldIndex : currentError.fieldIndex;

        if (referenceRow === -1 || referenceField === -1) {
            // No current position, go to first error
            nextError = sortedErrors[0];
        } else {
            // Find next error after current position
            nextError = findNextErrorAfterPosition(referenceRow, referenceField);
        }

        if (nextError) {
            setCurrentError({ rowIndex: nextError.rowIndex, fieldIndex: nextError.fieldIndex });

            scrollToError(nextError.rowIndex, nextError.fieldIndex);
            setCurrentFocusedCell({
                rowIndex: nextError.rowIndex,
                fieldIndex: nextError.fieldIndex
            });
            focusCell(nextError.rowIndex, nextError.fieldIndex);

            // Also update focused cell to match error cell
            setCurrentFocusedCell({ rowIndex: nextError.rowIndex, fieldIndex: nextError.fieldIndex });
        }
    }, [currentFocusedCell, currentError, getSortedErrorCells, findNextErrorAfterPosition]);

    const handlePreviousError = useCallback(() => {
        const sortedErrors = getSortedErrorCells();
        if (sortedErrors.length === 0) return;

        let previousError;

        // Use current focused cell position, fallback to current error
        const referenceRow = currentFocusedCell.rowIndex !== -1 ? currentFocusedCell.rowIndex : currentError.rowIndex;
        const referenceField = currentFocusedCell.fieldIndex !== -1 ? currentFocusedCell.fieldIndex : currentError.fieldIndex;

        if (referenceRow === -1 || referenceField === -1) {
            // No current position, go to last error
            previousError = sortedErrors[sortedErrors.length - 1];
        } else {
            // Find previous error before current position
            previousError = findPreviousErrorBeforePosition(referenceRow, referenceField);
        }

        if (previousError) {
            setCurrentError({ rowIndex: previousError.rowIndex, fieldIndex: previousError.fieldIndex });
            scrollToError(previousError.rowIndex, previousError.fieldIndex);

            setCurrentFocusedCell({
                rowIndex: previousError.rowIndex,
                fieldIndex: previousError.fieldIndex
            });
            focusCell(previousError.rowIndex, previousError.fieldIndex);

            // Also update focused cell to match error cell
            setCurrentFocusedCell({ rowIndex: previousError.rowIndex, fieldIndex: previousError.fieldIndex });
        }
    }, [currentFocusedCell, currentError, getSortedErrorCells, findPreviousErrorBeforePosition]);

    // Scrolling to and highlighting an error field
    const scrollToError = (rowIndex, fieldIndex) => {
        if (fieldIndex === -1) return;
        const field = columns[fieldIndex].field;
        const target =
            field === "LastRecordDT" || field === "NextTaskDT"
                ? document.getElementById(`date-${rowIndex}-${fieldIndex}`)
                : field === "Mobile"
                    ? document.getElementById(`mobile-${rowIndex}-${fieldIndex}`)
                    : inputRefs.current[rowIndex]?.[fieldIndex]?.current;

        if (!target) return;

        inputRefs.current.flat().forEach(ref => ref?.current?.classList.remove("error-highlight"));
        document.querySelectorAll('[id^="date-"]').forEach(el => el.classList.remove("error-highlight"));
        document.querySelectorAll('[id^="mobile-"]').forEach(el => el.classList.remove("error-highlight"));

        target.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "nearest"
        });
        target.classList.add("error-highlight");
    }

    const getConnType = (connType) => {
        if (!connType) return '';
        const types = {
            P: 'Future Prospect',
            C: 'Existing Customer',
            S: 'Supplier'
        };
        return ['P', 'C', 'S']
            .filter(key => connType.includes(key))
            .map(key => types[key])
            .join(', ');
    };

    //Comp render function
    const renderInputField = useCallback((column, row, rowIndex, fieldIndex) => {
        const fieldHandlers = {
            No: () => (rowIndex + 1).toString(),
            POCUserID: () => users.find((u) => String(u.userID) === row.POCUserID)?.name || '',
            RegionID: () => regions.find((r) => r.RID === row.RegionID)?.Rgn || '',
            ConnType: () => getConnType(row.ConnType)
        };

        const value = fieldHandlers[column.field]?.() ?? row[column.field] ?? '';

        const cellKey = getCellKey(rowIndex, fieldIndex);
        const hasError = errorCells.has(cellKey);
        const isCurrentError =
            currentError.rowIndex === rowIndex &&
            currentError.fieldIndex === fieldIndex;

        const baseClassName = `
            w-full border sahi-radius text-sm
            ${hasError ? "border-sahi-red" : "border-sahi-grey"}
            ${isCurrentError ? "" : ""}
            `;

        const commonProps = {
            ref: inputRefs.current[rowIndex][fieldIndex],
            value,
            onChange: (e) =>
                handleInputChange(rowIndex, fieldIndex, e.target.value),
            className: baseClassName,
        };

        const getOptions = () => {
            if (column.field === "Country") return memoizedCountries.map((c) => c.Country);
            if (column.field === "State" && row.CountryID === 1)
                return memoizedStates.filter((s) => s.CountryID === 1).map((s) => s.State);
            if (column.field === "POCUserID") return [...users.map((u) => u.name)];
            if (column.field === "RegionID") return regions.map(rgn => rgn.Rgn);
            if (column.field === "ConnType") {
                return isInqOnlyMode
                    ? ['Future Prospect', 'Existing Customer']
                    : ['Future Prospect', 'Existing Customer', 'Supplier'];
            }
            return [];
        };


        switch (column.type) {
            case "select":
                return (
                    <Select
                        value={value || "__none__"}
                        onValueChange={(val) => {
                            let finalValue = val === "__none__" ? "" : val;

                            // Convert display value to API format for Connection Type
                            if (column.field === "ConnType") {
                                const typeLetters = [];
                                if (finalValue.includes('Future Prospect')) typeLetters.push('P');
                                if (finalValue.includes('Existing Customer')) typeLetters.push('C');
                                if (finalValue.includes('Supplier')) typeLetters.push('S');
                                finalValue = typeLetters.join('');
                            }

                            handleInputChange(rowIndex, fieldIndex, finalValue);
                        }}
                    >
                        <SelectTrigger
                            ref={inputRefs.current[rowIndex][fieldIndex]}
                            className={cn("sahi-input", baseClassName)}
                        >
                            <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                {/* extra empty option */}
                                <SelectItem value="__none__">Select</SelectItem>

                                {/* your dynamic options */}
                                {getOptions().map((option, i) => (
                                    <SelectItem key={i} value={option}>
                                        {option}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>

                );


            case "textarea":
                return (
                    <Textarea
                        {...commonProps}
                        className={`${baseClassName} resize-none`}
                        rows={2}
                    />
                );

            case "date": {
                let dateValue = "";
                if (value) {
                    const d = new Date(value);
                    if (!isNaN(d)) dateValue = format(d, "yyyy-MM-dd'T'HH:mm:ss");
                }

                const today = new Date();

                // default undefined
                let minDate = undefined;
                let maxDate = undefined;

                if (fieldIndex === 12) {
                    maxDate = today; // ReceivedOnDT: max date = today (past dates only)
                } else if (fieldIndex === 14) {
                    maxDate = today; // LastRecordDT: max date = today (past dates only)
                } else if (fieldIndex === 16) {
                    minDate = today; // NextTaskDT: min date = today (current and future dates only)
                }
                return (
                    <div
                        id={`date-${rowIndex}-${fieldIndex}`}
                    >
                        <DatePicker
                            value={dateValue}
                            onChange={(date) => {
                                if (date) {
                                    const year = date.getFullYear();
                                    const month = String(date.getMonth() + 1).padStart(2, '0');
                                    const day = String(date.getDate()).padStart(2, '0');
                                    const dateVal = `${year}-${month}-${day}T00:00:00`;
                                    handleInputChange(rowIndex, fieldIndex, dateVal);
                                } else {
                                    handleInputChange(rowIndex, fieldIndex, "");
                                }
                            }}
                            minDate={minDate}
                            maxDate={maxDate}
                            placeholder="Select Date"
                            fieldId={`date-${rowIndex}-${fieldIndex}`}
                            id={`date-${rowIndex}-${fieldIndex}`}
                            ref={commonProps.ref}

                        />
                    </div>
                );
            }

            case "number":
                return (
                    <Input {...commonProps} type="text" placeholder="" />
                );

            case "email":
                return <Input {...commonProps} type="email" placeholder="email@example.com" />;

            case "dynamic": // special case: State can be select/text
                if (column.field === "State" && row.CountryID === 1) {
                    return (
                        <Select
                            value={value}
                            onValueChange={(val) => handleInputChange(rowIndex, fieldIndex, val)}
                        >
                            <SelectTrigger
                                ref={inputRefs.current[rowIndex][fieldIndex]}
                                className={cn("sahi-input", baseClassName)}
                            >
                                <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                                {states
                                    .filter((s) => s.CountryID === 1)
                                    .map((s, i) => (
                                        <SelectItem key={i} value={s.State}>
                                            {s.State}
                                        </SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                    );
                } else {
                    return <Input {...commonProps} type="text" />;
                }

            case "text":
            default:
                if (column.field === "No") {
                    return <span className="mb-5">{value}</span>;
                }

                // Special handling for Mobile field
                if (column.field === "Mobile") {
                    return (
                        <div id={`mobile-${rowIndex}-${fieldIndex}`}>
                            <MobileInput
                                fieldID={`mobile-${rowIndex}-${fieldIndex}`}
                                label=""
                                placeholder="Enter mobile number"
                                value={value}
                                onChange={(e) => {
                                    const rawValue = e.target.value;

                                    // If pasted value starts with '+' (e.g., +918908908902)
                                    if (rawValue.startsWith('+')) {
                                        const cleanDigits = rawValue.replace(/\D/g, ''); // Remove everything except digits

                                        // Sort countries by CC length (descending) to match longest code first (e.g., 1246 before 1)
                                        const matchedCountry = [...memoizedCountries]
                                            .sort((a, b) => String(b.CC).length - String(a.CC).length)
                                            .find(c => cleanDigits.startsWith(String(c.CC)));

                                        if (matchedCountry) {
                                            const mobileOnly = cleanDigits.substring(String(matchedCountry.CC).length);

                                            setEditableData(prevData => {
                                                const newData = [...prevData];
                                                newData[rowIndex] = {
                                                    ...newData[rowIndex],
                                                    CountryID: matchedCountry.CountryID,
                                                    CC: matchedCountry.CC,
                                                    Country: matchedCountry.Country,
                                                    Mobile: mobileOnly,
                                                    State: "", // Reset state when country changes
                                                    StateID: 0
                                                };
                                                return newData;
                                            });

                                            setChangedCells(prev => {
                                                const newSet = new Set(prev);
                                                newSet.add(`${rowIndex},${fieldIndex}`); // Validate Mobile
                                                const countryIdx = columns.findIndex(c => c.field === 'Country');
                                                if (countryIdx !== -1) newSet.add(`${rowIndex},${countryIdx}`); // Validate Country
                                                return newSet;
                                            });
                                            return; // Exit early as we've handled the full paste
                                        }
                                    }

                                    if (e.target.name === 'mobile-' + rowIndex + '-' + fieldIndex || e.target.name === 'mobile') {
                                        handleInputChange(rowIndex, fieldIndex, rawValue);
                                        setChangedCells(prev => {
                                            const newSet = new Set(prev);
                                            newSet.add(`${rowIndex},${fieldIndex}`);
                                            return newSet;
                                        });
                                    } else if (e.target.name === 'country') {
                                        // Handle country change from mobile input
                                        const countryFieldIndex = columns.findIndex(col => col.field === 'Country');
                                        if (countryFieldIndex !== -1) {
                                            handleInputChange(rowIndex, countryFieldIndex, e.target.value);
                                        }
                                    } else if (e.target.name === 'state') {
                                        // Handle state reset from mobile input
                                        const stateFieldIndex = columns.findIndex(col => col.field === 'State');
                                        if (stateFieldIndex !== -1) {
                                            handleInputChange(rowIndex, stateFieldIndex, e.target.value);
                                        }
                                    }
                                }}
                                selectedCountryCode={row.CC || "91"} // Default to India (1) if not set
                                onCountryCodeChange={(countryID) => {
                                    const country = memoizedCountries.find(c => c.CC === countryID);
                                    if (country) {
                                        // Update state directly to ensure CountryID and CC are set
                                        setEditableData(prevData => {
                                            const newData = [...prevData];
                                            newData[rowIndex] = {
                                                ...newData[rowIndex],
                                                CountryID: country.CountryID,
                                                CC: country.CC,
                                                Country: country.Country,
                                                State: "", // Reset state on country change
                                                StateID: 0
                                            };
                                            return newData;
                                        });

                                        // Trigger validation for dependent fields
                                        setChangedCells(prev => {
                                            const newSet = new Set(prev);
                                            newSet.add(`${rowIndex},${fieldIndex}`); // Mobile field

                                            const countryColIdx = columns.findIndex(c => c.field === 'Country');
                                            if (countryColIdx !== -1) newSet.add(`${rowIndex},${countryColIdx}`);

                                            return newSet;
                                        });
                                    }
                                }}
                                className="w-full"
                                ref={inputRefs.current[rowIndex][fieldIndex]}
                            />
                        </div>
                    );
                }

                return <Input {...commonProps} type="text" />;
        }


    }, [errorCells, cellErrors, currentError, handleInputChange, getCellKey, memoizedCountries, editableData]);

    const toggleIgnore = () => {
        setIngoreErrors(prev => !prev);
    }

    const openDialog = () => {
        setSaveDialog(true)
    }

    const closeDialog = () => {
        setSaveDialog(false)
    }
    const errorFreeRows = useMemo(() => getErrorFreeRows(), [getErrorFreeRows]);

    const getDialogContent = () => {
        const errorFreeRowsCount = errorFreeRows.length;
        const totalRows = editableData.length;
        const hasErrors = errorCells.size > 0;

        if (ignoreErrors && hasErrors) {
            return {
                title: "Import Valid Contacts",
                message: `Found ${totalRows - errorFreeRowsCount} ${pluralize(totalRows - errorFreeRowsCount, "record", "records")} with errors that will be skipped. Are you sure you wish to import ${errorFreeRowsCount} valid ${pluralize(errorFreeRowsCount, "contact", "contacts")}?`,
                confirmText: `Import ${errorFreeRowsCount} ${pluralize(errorFreeRowsCount, "Contact", "Contacts")}`
            };
        } else {
            return {
                title: "Contact Import",
                message: `Are you sure you want to import all ${totalRows} ${pluralize(totalRows, "contact", "contacts")}?`,
                confirmText: `Import All ${totalRows} ${pluralize(totalRows, "Contact", "Contacts")}`
            };
        }
    };

    const dialogContent = getDialogContent();

    return (
        <div className="h-[95vh] flex flex-col space-y-3 lg:space-y-6 overflow-hidden scrollbar-hide">
            {/* Header */}
            <div className="flex justify-between items-center gap-3">
                <div className="flex items-center flex-row justify-between">
                    <h1 className="hidden lg:block text-3xl font-bold text-text-color mr-4 web-header-name">
                        Contact Import (Corrections)
                    </h1>
                    <div className="fixed right-6 flex justify-between items-center gap-3 z-500">
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={toggleIgnore}>
                                        <div className='f-row items-center justify-between gap-2'>
                                            <Checkbox
                                                checked={ignoreErrors}
                                                disabled={errorCells.size === 0}
                                                className={checkBoxCss}
                                            />
                                            <span className="font-medium text-base text-black ">Ignore Errors</span>
                                        </div>
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>
                                        {errorCells.size > 0
                                            ? `${errorFreeRows.length} out of ${editableData.length} rows are valid and can be imported`
                                            : "No errors found"}
                                    </p>
                                </TooltipContent>
                            </Tooltip>

                            {/* Previous Error */}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="inline-block">
                                        <Button
                                            variant="secondary"
                                            className={`${erroNavBtnCss} sahi-primary-button`}
                                            onClick={handlePreviousError}
                                            disabled={getPrevErrorButtonDisabled()}
                                        >
                                            <FontAwesomeIcon icon={faChevronUp} />
                                            Previous Error
                                        </Button>
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Go to Previous Error</p>
                                </TooltipContent>
                            </Tooltip>

                            {/* Next Error */}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="inline-block">
                                        <Button
                                            variant="secondary"
                                            className={`${erroNavBtnCss} sahi-primary-button`}
                                            onClick={handleNextError}
                                            disabled={getNextErrorButtonDisabled()}

                                        >
                                            <FontAwesomeIcon icon={faChevronDown} />
                                            Next Error
                                        </Button>
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Go to Next Error</p>
                                </TooltipContent>
                            </Tooltip>

                            {/* Save Button */}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="inline-block">
                                        <Button
                                            className={cn(
                                                "!bg-sahi-green text-base text-white py-[6px] px-[10px] sahi-radius disabled:cursor-not-allowed disabled:opacity-55",
                                                "hover:!bg-sahi-white hover:!text-sahi-green border border-sahi-green"
                                            )}
                                            onClick={openDialog}
                                            disabled={
                                                (!ignoreErrors && errorCells.size > 0) ||
                                                errorFreeRows.length === 0
                                            }
                                        >
                                            <FontAwesomeIcon icon={faCheck} />
                                            {`Save`}
                                        </Button>
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>
                                        {errorFreeRows.length === 0
                                            ? "No valid rows to save"
                                            : !ignoreErrors && errorCells.size > 0
                                                ? "Fix all errors or enable 'Ignore Errors' to save Valid rows"
                                                : ignoreErrors && errorCells.size > 0
                                                    ? `Save ${errorFreeRows.length} Valid rows`
                                                    : "Save all rows"}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </div>
            </div>

            {/* Card */}
            <Card className="bg-sahi-white flex-1 flex flex-col overflow-hidden max-h-[calc(100vh-20px)]">
                <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
                    {apiError && (
                        <div className="bg-red-50 border border-red-200 text-sahi-red px-4 py-3 sahi-radius mb-4">
                            {apiError}
                        </div>
                    )}

                    {editableData.length === 0 && (
                        <div className="text-gray-600 mb-4 text-center py-8">
                            No data loaded. Please upload a valid Excel file.
                        </div>
                    )}

                    {editableData.length > 0 && (
                        <VirtualizedTable
                            data={editableData}
                            columns={columns}
                            onInputChange={handleInputChange}
                            errorCells={errorCells}
                            cellErrors={cellErrors}
                            currentError={currentError}
                            getCellKey={getCellKey}
                            renderInputField={renderInputField}
                        />
                    )}
                </CardContent>
            </Card>

            {saveDialog && (
                <Dialog
                    open={saveDialog}
                    onOpenChange={(e) => {
                        setSaveDialog(!saveDialog)
                    }}
                >
                    <DialogContent className="w-full max-w-6xl pb-4 sahi-radius overflow-hidden">
                        <DialogHeader className="dialog-header flex items-center justify-between px-4 pt-4">
                            <div className="flex items-center gap-2">
                                <DialogTitle className="font-semibold text-lg">
                                    {dialogContent.title}
                                </DialogTitle>
                            </div>

                            <DialogClose className="p-1 sahi-radius">
                                <X className="h-5 w-5" />
                            </DialogClose>
                        </DialogHeader>
                        <div className="px-4 ">
                            {dialogContent.message}
                        </div>

                        <div className="w-fit !sahi-radius px-4">
                            <div className="f-row gap-3  justify-between">
                                <Button
                                    onClick={handleAddClick}
                                    className="!w-full lg:w-fit "
                                >
                                    <Check className="h-4 w-4" />
                                    {dialogContent.confirmText}
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={closeDialog}
                                    className="!w-full lg:w-fit cls-btn"
                                >
                                    <X className="h-4 w-4" />
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};

export default CorrectExcel;